/**
 * <Cluster> — horizontal flex container with locked gap scale.
 *
 * Wraps onto multiple lines by default — that's the "cluster" semantic
 * (see Every Layout / utopia.fyi). For non-wrapping rows use `wrap={false}`.
 *
 * Spec: docs/design-system/03-spacing-layout.md
 */

import { forwardRef, type HTMLAttributes, type ElementType, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import type { StackGap } from './Stack';

export interface ClusterProps extends HTMLAttributes<HTMLDivElement> {
  /** Horizontal gap between children. Default `sm` (8px). */
  gap?: StackGap;
  /** Cross-axis alignment. Default `center`. */
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  /** Main-axis alignment. */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  /** Allow children to wrap across lines. Default `true`. */
  wrap?: boolean;
  as?: ElementType;
  children: ReactNode;
}

const GAP_CLASSES: Record<StackGap, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',   // default
  md: 'gap-3',   // 12px (tighter than Stack — horizontal usually needs less)
  lg: 'gap-4',
  xl: 'gap-6',
  '2xl': 'gap-8',
};

const ALIGN_CLASSES: Record<NonNullable<ClusterProps['align']>, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  baseline: 'items-baseline',
  stretch: 'items-stretch',
};

const JUSTIFY_CLASSES: Record<NonNullable<ClusterProps['justify']>, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

export const Cluster = forwardRef<HTMLDivElement, ClusterProps>(function Cluster(
  { gap = 'sm', align = 'center', justify, wrap = true, as: Tag = 'div', className, children, ...rest },
  ref,
) {
  return (
    <Tag
      ref={ref as never}
      className={cn(
        'flex',
        wrap && 'flex-wrap',
        GAP_CLASSES[gap],
        ALIGN_CLASSES[align],
        justify && JUSTIFY_CLASSES[justify],
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
});

Cluster.displayName = 'Cluster';

export default Cluster;
